/**
 * ProductListPage — 상품관리 목록 (단가·수량·라인토탈 cascading 계산).
 *
 * 【역할】
 * - 상품 마스터 CRUD — 편집 모달 기반 벌크 저장
 * - 단가 × 재고수량 = 라인토탈 실시간 cascading 계산 데모
 *
 * 【주요 기능】
 * - 검색 (상품명/카테고리/활성여부)
 * - 편집 모달 (unitPrice / stockQty 변경 시 lineTotal 실시간 재계산)
 * - 최소재고 미만 시 경고 Tag (stock-alert)
 * - Excel Export
 * - 저장 시 ds_list 변경분만 ds_product 로 전송 (C/U/D 상태 추적)
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useCodes / useLabel / useExcel / useI18nTickStore
 *
 * 【Backend RPC serviceName】
 * - product/searchList  (action=search) — 조회 (outds: ds_product → ds_list)
 * - product/save        (action=save)   — 저장 (inds: ds_product = 변경분만)
 *
 * 【DataSet 구조】
 * - ds_list: 상품 목록 (PK: productId, 변경추적 C/U/D)
 *
 * 【사용처】 router/index.tsx — `{ path: 'product' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageProductList.vue
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, InputNumber, Select, Table, Modal } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useCodes } from '@/framework/useCodes'
import { useExcel } from '@/framework/useExcel'
import { useLabel } from '@/framework/useLabel'
import { buildExcelHeaders } from '@/framework/grid/excelHeaders'
import RowStateTag from '@/components/common/RowStateTag'
import { useI18nTickStore } from '@/framework/useLocale'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'

interface ProductRow {
  /** 상품 PK — Oracle SEQ 채번 */
  productId?: string
  /** 상품명 — 필수 */
  productName?: string
  /** 카테고리 — 공통코드 PRODUCT_CATEGORY (ELECTRONICS/CLOTHING/etc.) */
  category?: string
  /** 단위 — 공통코드 UNIT (EA/KG/...) */
  unit?: string
  /** 단가(원) — InputNumber, 0 이상 */
  unitPrice?: number
  /** 재고수량 — InputNumber, 0 이상. minStockQty 미만 시 경고 표시 */
  stockQty?: number
  /** 최소재고 — stockQty 가 이 값 미만이면 그리드에 빨강 강조 + warn 메시지 */
  minStockQty?: number
  /** 설명 — TextArea 자유 입력 */
  description?: string
}

/** 통화/수량용 ko-KR 천단위 콤마 포맷터 (null/undefined → '0'). */
const fmt = (v?: number) => (v == null ? '0' : new Intl.NumberFormat('ko-KR').format(v))

/**
 * ProductListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ProductListPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const { exportToExcel } = useExcel()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_list = $createDataSet<ProductRow>([])

  const [searchForm] = Form.useForm()
  const [editOpen, setEditOpen] = useState(false)
  const [editMode, setEditMode] = useState<'C' | 'U'>('C')
  const [editingIndex, setEditingIndex] = useState<number>(-1)
  const [editForm] = Form.useForm<ProductRow>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)
  // cascading 계산: 편집 폼의 unitPrice/stockQty → lineTotal
  const [lineTotal, setLineTotal] = useState<number>(0)

  useEffect(() => {
    // ─── 공통코드 lazy load (PRODUCT_CATEGORY/UNIT) ─
    loadCodes(['PRODUCT_CATEGORY', 'UNIT'])
  }, [loadCodes])

  /** 검색 — 폼 조건으로 ds_list (상품 목록) 조회 + 선택 해제. */
  const fn_search = async () => {
    // ─── 검색 조건 빌드 + 트랜잭션 ─
    // 1) form 값 수집 (안 채운 필드는 undefined → 백엔드 LIKE 매칭 skip)
    const params = searchForm.getFieldsValue()
    // 2) 로딩 표시
    setLoading(true)
    // 3) RPC — outds 로 ds_product 페이로드를 ds_list 에 직접 바인딩
    const res = await $transaction({
      id: 'select',
      service: 'product/searchList',
      action: 'search',
      params,
      outds: { ds_product: ds_list }
    })
    setLoading(false)
    // 4) 성공 시 선택 초기화
    if (res.success) setSelectedRowKeys([])
  }

  /** 검색 초기화 — 폼/list/선택 모두 reset. */
  const fn_reset = () => {
    // ─── 폼/DS/선택 초기화 ─
    searchForm.resetFields()
    ds_list.clear()
    setSelectedRowKeys([])
  }

  /** 편집 폼 onValuesChange 콜백 — unitPrice/stockQty 변경 시 lineTotal cascading 계산. */
  const onEditValuesChange = (_: Partial<ProductRow>, all: ProductRow) => {
    // ─── cascading 계산: unitPrice × stockQty = lineTotal ─
    // 1) 숫자 강제 변환 (문자열/null 방어)
    const up = Number(all.unitPrice ?? 0)
    const sq = Number(all.stockQty ?? 0)
    // 2) lineTotal state 갱신 → 모달 하단 disabled Input 자동 표시
    setLineTotal(up * sq)
    // 3) 재고 경고 (현재는 onSubmit 시점 일괄 알림. 실시간 알림은 너무 시끄러움)
    const min = Number(all.minStockQty ?? 0)
    if (min > 0 && sq < min) {
      // 단발성 경고 — 너무 시끄럽지 않게 form 하단 helper 로 대체 가능
    }
  }

  /** 신규 — 편집 모달 C 모드 + 기본값(ELECTRONICS/EA/0/0/10). */
  const fn_add = () => {
    // ─── 편집 모달 C 모드 진입 ─
    // 1) 모드/인덱스 reset
    setEditMode('C')
    setEditingIndex(-1)
    editForm.resetFields()
    // 2) 합리적 기본값 + minStockQty=10 (안전재고 정책)
    editForm.setFieldsValue({
      category: 'ELECTRONICS',
      unit: 'EA',
      unitPrice: 0,
      stockQty: 0,
      minStockQty: 10
    })
    // 3) lineTotal 초기화 (0 × 0 = 0)
    setLineTotal(0)
    setEditOpen(true)
  }

  /** 수정 — 단일 선택 행을 편집 모달 U 모드로 로딩 + lineTotal 사전 계산. */
  const fn_edit = () => {
    // ─── 단일 선택 검증 + U 모드 로딩 ─
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_EDIT_ROW')
      return
    }
    const idx = visibleRows.findIndex((r) => r._rowId === selectedRowKeys[0])
    if (idx < 0) return
    setEditMode('U')
    setEditingIndex(idx)
    // 폼에 행 데이터 주입 + lineTotal 사전 계산 (모달 open 즉시 표시되도록)
    const data = visibleRows[idx].data
    editForm.setFieldsValue(data)
    setLineTotal(Number(data.unitPrice ?? 0) * Number(data.stockQty ?? 0))
    setEditOpen(true)
  }

  /** 편집 모달 OK — 재고 경고 알림 + ds 반영 (C/U 분기). */
  const fn_editSubmit = async () => {
    // ─── 폼 검증 + 재고 경고 + ds 반영 ─
    // 1) AntD Form validate
    const values = await editForm.validateFields()
    // 2) 재고 경고 알림 (원본 onCellChange 로직 이식 — stockQty < minStockQty)
    if (
      values.minStockQty != null &&
      values.stockQty != null &&
      values.stockQty < values.minStockQty
    ) {
      $msg.warn(`재고수량(${values.stockQty})이 최소재고(${values.minStockQty}) 미만입니다.`)
    }
    // 3) C — addRow / U — visible→actual 매핑 후 updateRow
    if (editMode === 'C') {
      ds_list.addRow(values)
    } else {
      const targetId = visibleRows[editingIndex]?._rowId
      const actualIdx = ds_list.rows.findIndex((r) => r._rowId === targetId)
      if (actualIdx >= 0) ds_list.updateRow(actualIdx, values)
    }
    setEditOpen(false)
  }

  /** 삭제 — 선택 행 일괄 deleteRow (확인 다이얼로그). */
  const fn_delete = () => {
    // ─── 선택 검증 + confirm + 일괄 삭제 ─
    if (!selectedRowKeys.length) {
      $msg.byId('MSG_SELECT_DELETE_ROW')
      return
    }
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      for (const key of selectedRowKeys) {
        const actualIdx = ds_list.rows.findIndex((r) => r._rowId === key)
        if (actualIdx >= 0) ds_list.deleteRow(actualIdx)
      }
      setSelectedRowKeys([])
    })
  }

  /** 저장 — 변경분(ds_list)을 product/save 로 일괄 전송. */
  const fn_save = async () => {
    // ─── dirty 체크 + 벌크 저장 ─
    if (!ds_list.isDirty) {
      $msg.byId('MSG_NO_CHANGES')
      return
    }
    // inds: 변경분만 / outds: 채번 PK 회신
    const res = await $transaction({
      id: 'save',
      service: 'product/save',
      action: 'save',
      inds: { ds_product: ds_list },
      outds: { ds_product: ds_list }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search()
    }
  }

  /** 취소 — 변경 폐기 후 재조회. */
  const fn_cancel = () => fn_search()

  /** Excel Export — 화면 표시 행을 i18n 헤더로 xlsx 출력. */
  const fn_export = () => {
    exportToExcel(
      visibleRows.map((r) => r.data as unknown as Record<string, unknown>),
      buildExcelHeaders(
        [
          { key: 'productId', labelKey: 'COL_PRODUCT_ID', fallback: '상품ID', width: 12 },
          { key: 'productName', labelKey: 'COL_PRODUCT_NAME', fallback: '상품명', width: 25 },
          { key: 'category', labelKey: 'COL_CATEGORY', fallback: '카테고리', width: 12 },
          { key: 'unit', labelKey: 'COL_UNIT', fallback: '단위', width: 8 },
          { key: 'unitPrice', labelKey: 'COL_UNIT_PRICE', fallback: '단가', width: 14 },
          { key: 'stockQty', labelKey: 'COL_STOCK_QTY', fallback: '재고수량', width: 10 },
          { key: 'minStockQty', labelKey: 'COL_MIN_STOCK_QTY', fallback: '최소재고', width: 10 },
          { key: 'description', labelKey: 'COL_DESCRIPTION', fallback: '설명', width: 30 }
        ],
        t
      ),
      t('FILE_PRODUCT_LIST', 'products.xlsx')
    )
  }

  const visibleRows = ds_list.getVisibleRows()

  const columns: ColumnsType<(typeof visibleRows)[number]> = useMemo(
    () => [
      { title: t('COL_PRODUCT_ID', '상품ID'), dataIndex: ['data', 'productId'], width: 90 },
      { title: t('COL_PRODUCT_NAME', '상품명'), dataIndex: ['data', 'productName'], width: 180 },
      {
        title: t('COL_CATEGORY', '카테고리'),
        dataIndex: ['data', 'category'],
        width: 100,
        render: (v: string) => getCodeName('PRODUCT_CATEGORY', v)
      },
      {
        title: t('COL_UNIT', '단위'),
        dataIndex: ['data', 'unit'],
        width: 70,
        render: (v: string) => getCodeName('UNIT', v)
      },
      {
        title: t('COL_UNIT_PRICE', '단가'),
        dataIndex: ['data', 'unitPrice'],
        width: 100,
        align: 'right',
        render: (v: number) => fmt(v)
      },
      {
        title: t('COL_STOCK_QTY', '재고수량'),
        dataIndex: ['data', 'stockQty'],
        width: 100,
        align: 'right',
        render: (_: number, r) => {
          const q = r.data.stockQty ?? 0
          const m = r.data.minStockQty ?? 0
          const low = m > 0 && q < m
          return (
            <span style={{ color: low ? '#ff4d4f' : undefined, fontWeight: low ? 700 : undefined }}>
              {fmt(q)}
            </span>
          )
        }
      },
      {
        title: t('COL_LINE_TOTAL', '라인토탈'),
        key: 'lineTotal',
        width: 120,
        align: 'right',
        render: (_: unknown, r) => fmt(Number(r.data.unitPrice ?? 0) * Number(r.data.stockQty ?? 0))
      },
      {
        title: t('COL_MIN_STOCK_QTY', '최소재고'),
        dataIndex: ['data', 'minStockQty'],
        width: 90,
        align: 'right'
      },
      { title: t('COL_DESCRIPTION', '설명'), dataIndex: ['data', 'description'] },
      {
        title: t('COL_STATUS', '상태'),
        dataIndex: '_state',
        width: 70,
        render: (s: string) => <RowStateTag state={s} updateColor="gold" />
      }
    ],
    [getCodeName, t]
  )

  useEffect(() => {
    // ─── 초기 로드 (mount 1회) ─
    // 라이프사이클: mount → fn_search → ds_list 채움 → 그리드 렌더 + summary 합계 계산
    fn_search()
  }, []) // deps 비움 — 1회만 실행

  return (
    <>
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          onReset: fn_reset,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <FormInput placeholder={t('PH_PRODUCT_NAME', '상품명')} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="category" label={t('LBL_CATEGORY', '카테고리')}>
                <FormSelect
                  options={getOptions('PRODUCT_CATEGORY')}
                  style={{ minWidth: 160 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
              <Form.Item name="unit" label={t('LBL_UNIT', '단위')}>
                <FormSelect
                  options={getOptions('UNIT')}
                  style={{ minWidth: 120 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
            </>
          )
        }}
        toolbar={{
          onSearch: fn_search,
          onAdd: fn_add,
          onEdit: fn_edit,
          onDelete: fn_delete,
          onSave: fn_save,
          onCancel: fn_cancel,
          onExport: fn_export,
          loading,
          dirty: ds_list.isDirty
        }}
      >
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys)
          }}
          summary={(rows) => {
            const total = rows.reduce(
              (acc, r) => acc + Number(r.data.unitPrice ?? 0) * Number(r.data.stockQty ?? 0),
              0
            )
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    <strong>{t('LBL_TOTAL_AMT', '합계')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{fmt(total)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={3} />
                </Table.Summary.Row>
              </Table.Summary>
            )
          }}
        />
      </AppPage>

      <Modal
        open={editOpen}
        title={editMode === 'C' ? '상품 추가' : '상품 수정'}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        okText="확인"
        cancelText="취소"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onValuesChange={onEditValuesChange}>
          {editMode === 'U' && (
            <Form.Item name="productId" label={t('COL_PRODUCT_ID', '상품ID')}>
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item
            name="productName"
            label={t('COL_PRODUCT_NAME', '상품명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="category"
            label={t('COL_CATEGORY', '카테고리')}
            rules={[{ required: true }]}
          >
            <Select options={getOptions('PRODUCT_CATEGORY')} />
          </Form.Item>
          <Form.Item name="unit" label={t('COL_UNIT', '단위')}>
            <Select options={getOptions('UNIT')} />
          </Form.Item>
          <Form.Item
            name="unitPrice"
            label={t('COL_UNIT_PRICE', '단가')}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} formatter={(v) => fmt(Number(v))} />
          </Form.Item>
          <Form.Item name="stockQty" label={t('COL_STOCK_QTY', '재고수량')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('COL_LINE_TOTAL', '라인토탈')}>
            <Input value={fmt(lineTotal)} disabled />
          </Form.Item>
          <Form.Item name="minStockQty" label={t('COL_MIN_STOCK_QTY', '최소재고')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label={t('COL_DESCRIPTION', '설명')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
